import React from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, Megaphone, Package, Share2 } from "lucide-react";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import { useStoreLink } from "lib/store-link";
import { useOpenBoost, naira } from "lib/boost";
import { AdsSummary } from "action/ads-action";
import { count } from "screens/partials/meta-ads/parts";

/** The store's public link with a Copy Link button, right under the greeting. */
export function StoreLinkCard({ link }: { link: ReturnType<typeof useStoreLink> }) {
  if (!link.url) return null;
  return (
    <div className="flex flex-col gap-3 rounded-20px bg-default-900 p-4 text-default-foreground sm:flex-row sm:items-center sm:justify-between md:px-5">
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-wider opacity-70">Your store link</div>
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-0.5 inline-flex max-w-full items-center gap-1.5 truncate font-display text-base font-semibold hover:underline md:text-lg"
        >
          <span className="truncate">{link.url.replace(/^https:\/\//, "")}</span>
          <ExternalLink className="h-4 w-4 flex-none opacity-70" />
        </a>
      </div>
      <Button onClick={link.copy} className="bg-card text-default-900 hover:bg-card/90">
        {link.copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
        Copy Link
      </Button>
    </div>
  );
}

interface GetStartedProps {
  hasProduct: boolean;
  hasPaidCampaign: boolean;
  link: ReturnType<typeof useStoreLink>;
}

/** "Get your first sale in 3 steps": ticks itself off as the vendor goes, and disappears when all three are done. */
export function GetStartedCard({ hasProduct, hasPaidCampaign, link }: GetStartedProps) {
  const openBoost = useOpenBoost();
  const steps = [
    {
      done: hasProduct,
      icon: <Package className="h-4 w-4" />,
      title: "Add your first product",
      hint: "Clear photos and prices help customers buy.",
      action: (
        <Button asChild size="md" variant={hasProduct ? "outline" : "default"}>
          <Link to="/add-product">{hasProduct ? "Add another" : "Add product"}</Link>
        </Button>
      ),
    },
    {
      done: link.copied,
      icon: <Share2 className="h-4 w-4" />,
      title: "Copy & share your store link",
      hint: "Post it on WhatsApp, Instagram and TikTok.",
      action: (
        <Button size="md" variant={link.copied ? "outline" : "default"} onClick={link.copy}>
          Copy Link
        </Button>
      ),
    },
    {
      done: hasPaidCampaign,
      icon: <Megaphone className="h-4 w-4" />,
      title: "Run your first Meta Ad",
      hint: "We put your store in front of new customers.",
      action: (
        <Button size="md" onClick={() => openBoost()}>
          Boost on Meta Ads
        </Button>
      ),
    },
  ];
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  return (
    <section className="rounded-20px border border-default-200 bg-card p-5 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-default-900">Get your first sale in 3 steps</h2>
          <p className="mt-0.5 text-sm text-default-600">
            {done} of {steps.length} done
          </p>
        </div>
      </div>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-default-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={done}
        aria-label="Steps done"
      >
        <div className="h-full rounded-full bg-chart-1 transition-[width]" style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <ol className="mt-5 grid gap-3 md:grid-cols-3">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className={cn(
              "flex flex-col gap-3 rounded-[14px] border p-4",
              step.done ? "border-transparent bg-default-50" : "border-default-200",
            )}
          >
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  "flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-semibold",
                  step.done ? "bg-success text-success-foreground" : "bg-default-900 text-default-foreground",
                )}
              >
                {step.done ? <Check className="h-4 w-4" strokeWidth={3} /> : index + 1}
              </span>
              <div className="min-w-0">
                <div className={cn("text-sm font-medium", step.done ? "text-default-500 line-through" : "text-default-900")}>
                  {step.title}
                </div>
                <p className="mt-0.5 text-xs text-default-600">{step.hint}</p>
              </div>
            </div>
            {!step.done && <div className="mt-auto">{step.action}</div>}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The dashboard's Meta Ads summary, for the same timeframe as the cards around it. */
export function MetaAdsWidget({ summary, timeframeLabel }: { summary?: AdsSummary | null; timeframeLabel: string }) {
  const openBoost = useOpenBoost();
  if (!summary?.has_paid_campaign) {
    return (
      <div className="flex h-full flex-col justify-between gap-4">
        <p className="text-sm text-default-600">
          Reach more people on Facebook and Instagram. Boost your store or a product and see the views, store visits and
          sales it brings, right here.
        </p>
        <div>
          <Button onClick={() => openBoost()}>
            <Megaphone className="mr-2 h-4 w-4" />
            Boost on Meta Ads
          </Button>
        </div>
      </div>
    );
  }
  const t = summary.totals;
  const rows: [string, string][] = [
    ["Ad spend", naira(t.spend)],
    ["Views", count(t.impressions)],
    ["Store visits", count(t.store_visits || t.clicks)],
    ["Added to cart", count(t.add_to_cart)],
    ["Purchases from ads", count(t.purchases)],
  ];
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3">
        {rows.map(([label, value], index) => (
          <div key={label} className={cn("rounded-[14px] bg-default-50 p-4", index === 0 && "col-span-2")}>
            <dt className="text-xs font-medium text-default-600">{label}</dt>
            <dd className="mt-1 font-display text-xl font-semibold tabular-nums text-default-900">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex items-center justify-between text-xs text-default-500">
        <span>{timeframeLabel}</span>
        <Link to="/meta-ads" className="font-medium text-default-700 hover:text-default-900">
          See all results →
        </Link>
      </div>
    </div>
  );
}
